class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('keydown', this.onKeyDown.bind(this), { capture: true });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onKeyDown(event) {
    const key = String(event.key || event.code || '').toUpperCase();
    if (!this.classList.contains('active')) return;

    if (key === 'ENTER' && event.target.matches('#CartDrawer-Checkout')) {
      event.preventDefault();
      const checkoutForm = event.target.form || document.getElementById(event.target.getAttribute('form'));
      if (checkoutForm?.requestSubmit) checkoutForm.requestSubmit(event.target);
      else event.target.click();
      return;
    }

    if (key !== 'TAB') return;

    const focusContainer = this.querySelector('.drawer__inner');
    const focusableElements = focusContainer
      ? getFocusableElements(focusContainer).filter((element) => (
        element.getClientRects().length > 0 &&
        (element.tagName === 'SUMMARY' || !element.closest('details:not([open])'))
      ))
      : [];
    if (!focusableElements.length) return;

    const currentIndex = focusableElements.indexOf(document.activeElement);
    const nextIndex = event.shiftKey
      ? (currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1)
      : (currentIndex < 0 || currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1);

    event.preventDefault();
    focusableElements[nextIndex].focus();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, .cart-button');
    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.cartDrawerBound === 'true') return;
      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.syncCartStateFromContents();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  syncCartStateFromContents() {
    const hasItems = Boolean(this.querySelector('.cart-item'));
    this.classList.toggle('is-empty', !hasItems);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', !hasItems);
    if (hasItems) {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
      this.querySelector('cart-drawer-items')?.scrollTo({ top: 0 });
    }
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) checkoutButton.disabled = !hasItems;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHTML = this.getSectionInnerHTML(html, section.selector);
      if (sectionElement && sectionHTML != null) sectionElement.innerHTML = sectionHTML;
    }));
    this.syncCartStateFromContents();
    this.setHeaderCartIconAccessibility();
    try {
      window.sessionStorage.setItem('ire5-cart-version', `${Date.now()}:${parsedState.item_count ?? ''}`);
    } catch (error) {
      // Cart rendering remains usable when session storage is unavailable.
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return section ? section.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
